<template lang="pug">
//- Textvorlagen for ONE owner: a shop when it is given a cmsId, ours when it is
//- not. Both settings screens render this same component, so the two lists can
//- never drift apart.
.message-presets(:class="nested ? 'is-nested settings-panel' : ''")
    //- A section of its own on our platform screen; a .settings-panel when it
    //- sits inside the shop's "Reusable text", so it reads as one boxed thing
    //- beside its peer rather than a run of text. The "New" button stays where
    //- every other one on the page is: left, under the head.
    template(v-if="nested")
        .settings-panel__head
            span.settings-panel__name {{ $t('Message presets') }}
        p.help.settings-panel__hint {{ ownerHint }}
    .section-intro.message-presets-intro(v-else)
        h4 {{ $t('Message presets') }}
        p.help {{ ownerHint }}

    .table-top
        el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="openPreset(null)")
            | {{ $t('New preset') }}

    el-table(ref="table" :data="presets" @row-click="openPreset")
        el-table-column(:label="$t('Name')" prop="name")
        el-table-column(:label="$t('Language')" width="180")
            template(slot-scope="{ row }")
                span(v-if="row.language")
                    span.i18n-flag.circle.fi.fis.mr-1(:class="`fi-${languageFlagFor(row.language)}`")
                    span {{ row.language | language(locale) }}
                span.muted(v-else) {{ $t('Any language') }}
        el-table-column(width="80" align="right")
            template(slot-scope="{ row }")
                el-button.delete-button(
                    plain circle size="mini" type="danger"
                    icon="el-icon-fa-trash-alt far"
                    @click.stop="$refs.deleteDialog.open(row)"
                )

    //- AFTER the list, because it configures what that list is used for. Above
    //- it, a shop with no presets was asked to set up suggestions for nothing.
    //-
    //- The VALUE belongs to the host, not to this component: a shop's mode is a
    //- CMS column that rides the settings form's own save, ours is a platform
    //- setting saved with the rest of them. This only edits it.
    el-form.suggestion-mode(label-position="top")
        el-form-item(:label="$t('AI suggestions')")
            el-radio-group(:value="mode" size="mini" @input="$emit('update:mode', $event)")
                el-radio-button(label="off") {{ $t('Off') }}
                el-radio-button(label="on-demand") {{ $t('On demand') }}
                el-radio-button(label="automatic") {{ $t('Automatic') }}
            p.help {{ suggestionModeHint }}

    confirm-delete-dialog(
        ref="deleteDialog"
        :title="$t('Delete preset')"
        :message="$t('The preset will be removed. Messages that already used it stay unchanged.')"
        @confirm="removePreset"
    )

    full-height-drawer(
        :visible.sync="drawer.visible"
        size="560px"
        :title="drawer.form.id ? $t('Edit preset') : $t('New preset')"
    )
        el-form(label-position="top")
            el-form-item(:label="$t('Name')")
                el-input(v-model="drawer.form.name" :placeholder="$t('e.g. Question about an order')")
            //- A hint for the picker's ordering, never a filter — see the
            //- conversation's availableMessagePresets().
            el-form-item(:label="$t('Language')")
                el-select(v-model="drawer.form.language" clearable filterable style="width: 100%" :placeholder="$t('Any language')")
                    el-option(
                        v-for="option in languageOptions"
                        :key="option.code"
                        :value="option.code"
                        :label="option.name"
                    )
                        //- Same option body as the i18n language picker
                        //- (components/LocalizationSettings.vue): flag, the name in
                        //- the admin's language, then the name as its own speakers
                        //- write it.
                        .flex-row
                            .col
                                span.i18n-flag.circle.fi.fis.mr-1(:class="`fi-${languageFlagFor(option.code)}`")
                                span {{ option.name }}
                            .col.gray-text
                                span {{ languageNativeNameFor(option.code) }}
                p.help {{ $t('Presets in the language a customer writes are offered first. Without one, the preset fits every language.') }}
            el-form-item(:label="$t('Text')")
                quill-editor.preset-editor(v-model="drawer.form.body" :options="quillConfig")
        template(#footer-left)
            el-button(round size="mini" icon="el-icon-fa-times fas" @click="drawer.visible = false") {{ $t('Cancel') }}
        template(#footer)
            //- "Ok", not "Save": this only puts the preset into the list. The
            //- page's Save is what writes it, together with everything else on
            //- the screen — so a half-finished edit is abandoned by leaving,
            //- not left behind in the database.
            el-button(
                round size="mini" type="success"
                icon="el-icon-fa-check fas"
                :disabled="!String(drawer.form.name || '').trim()"
                @click="applyPreset"
            ) {{ $t('Ok') }}
</template>

<i18n>
{
    "de": {
        "Message presets": "Textvorlagen",
        "Reusable replies your team can insert into a message instead of retyping them.": "Wiederverwendbare Antworten, die Ihr Team in eine Nachricht einfügen kann, statt sie neu zu tippen.",
        "Reusable replies we insert into a message instead of retyping them. Only our own desk sees these.": "Wiederverwendbare Antworten, die wir in eine Nachricht einfügen, statt sie neu zu tippen. Nur unser eigener Desk sieht sie.",
        "AI suggestions": "KI-Vorschläge",
        "Off": "Aus",
        "On demand": "Auf Abruf",
        "Automatic": "Automatisch",
        "Nobody is asked and nothing is billed.": "Es wird nichts angefragt und nichts berechnet.",
        "A button in the composer suggests the three best presets. Billed per request as AI tokens.": "Eine Schaltfläche im Editor schlägt die drei besten Textvorlagen vor. Wird je Anfrage als KI-Token berechnet.",
        "A button in the composer suggests the three best presets.": "Eine Schaltfläche im Editor schlägt die drei besten Textvorlagen vor.",
        "Suggests as soon as the E-Mail composer is opened. Asking again on the same thread costs nothing.": "Schlägt vor, sobald der E-Mail-Editor geöffnet wird. Erneutes Nachfragen im selben Verlauf kostet nichts.",
        "Ok": "Ok",
        "New preset": "Neue Textvorlage",
        "Edit preset": "Textvorlage bearbeiten",
        "Delete preset": "Textvorlage löschen",
        "The preset will be removed. Messages that already used it stay unchanged.": "Die Textvorlage wird entfernt. Bereits versendete Nachrichten bleiben unverändert.",
        "Name": "Name",
        "e.g. Question about an order": "z. B. Rückfrage zur Bestellung",
        "Language": "Sprache",
        "Any language": "Alle Sprachen",
        "Presets in the language a customer writes are offered first. Without one, the preset fits every language.": "Textvorlagen in der Sprache, in der eine Kundin oder ein Kunde schreibt, werden zuerst angeboten. Ohne Sprache passt die Vorlage zu jeder.",
        "Text": "Text",
        "Cancel": "Abbrechen",
        "Save": "Speichern"
    }
}
</i18n>

<script>
/**
 * One component, two hosts: the shop's communication settings tab and our own
 * /system/settings. The only difference is the owner, and `null` is a real
 * owner (us), not a missing one.
 */
import _ from 'lodash';

import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import FullHeightDrawer from '@/components/FullHeightDrawer';
import messagePresetQuillConfig from '@/quill/messagePresetConfig';
import { language as languageName, languageFlag, languageNativeName } from '@/filters';
import { useSettingsStore, useSetupStore } from '@/stores';

export default {
    name: 'MessagePresetManager',
    components: {
        'confirm-delete-dialog': ConfirmDeleteDialog,
        'full-height-drawer': FullHeightDrawer,
    },
    props: {
        // null = ours. Deliberately not `required`: the platform host renders
        // this component with no prop at all.
        cmsId: {
            type: Number,
            default: null,
        },
        // Rendered inside another section rather than as one: sub-heading instead
        // of a section heading, and a divider from the block above.
        nested: {
            type: Boolean,
            default: false,
        },
        // off | on-demand | automatic. Owned and persisted by the host — a
        // shop's is a CMS column, ours is a platform setting.
        mode: {
            type: String,
            default: 'off',
        },
        // The list itself is the HOST's too. This component edits it and emits
        // the result; the page's Save writes it with everything else on the
        // screen. Nothing here talks to the server.
        presets: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            drawer: {
                visible: false,
                form: { id: null, name: '', body: '', language: null },
            },
        };
    },
    computed: {
        isPlatform() {
            return this.cmsId === null;
        },
        locale() {
            return _.get(useSetupStore(), 'locale') || 'de';
        },
        /**
         * What a NEW preset starts in. A shop that runs i18n starts in the
         * language it publishes in by default; everything else starts in German,
         * which is what both desks write unless told otherwise. Falls back to
         * "any language" if that code is not on offer, so the select can never
         * show a value it has no option for.
         */
        defaultLanguage() {
            const form = _.get(useSettingsStore(), 'form') || {};
            let code = 'de';

            if (!this.isPlatform && form.i18n) {
                const languages = form.languages || [];
                code = _.get(_.find(languages, 'default') || languages[0], 'code') || 'de';
            }

            return _.some(this.languageOptions, { code }) ? code : null;
        },
        // The ticket placeholders are only offered where a ticket can actually
        // exist. Our own desk IS the platform ticket system; a shop has to have
        // booked the extension, or those two pills could only render blank.
        hasTickets() {
            return this.isPlatform || this.enabledExtensions.includes('tickets');
        },
        // Our own desk sells nothing over a counter, so {{ pos.name }} could only
        // ever render blank there — unlike the ticket pills, which our desk IS the
        // system for.
        hasPointOfSale() {
            return !this.isPlatform && this.enabledExtensions.includes('shop:point-of-sale');
        },
        enabledExtensions() {
            return _.get(useSetupStore(), 'enabledExtensions') || [];
        },
        quillConfig() {
            return messagePresetQuillConfig({
                hasTickets: this.hasTickets,
                hasPointOfSale: this.hasPointOfSale,
            });
        },
        ownerHint() {
            return this.isPlatform
                ? this.$t(
                      'Reusable replies we insert into a message instead of retyping them. Only our own desk sees these.'
                  )
                : this.$t('Reusable replies your team can insert into a message instead of retyping them.');
        },
        suggestionModeHint() {
            if (this.mode === 'automatic') {
                return this.$t('Suggests as soon as the E-Mail composer is opened. Asking again on the same thread costs nothing.');
            }

            if (this.mode === 'on-demand') {
                return this.isPlatform
                    ? this.$t('A button in the composer suggests the three best presets.')
                    : this.$t('A button in the composer suggests the three best presets. Billed per request as AI tokens.');
            }

            return this.$t('Nobody is asked and nothing is billed.');
        },
        /**
         * Which language a preset is WRITTEN in, so the picker can offer the
         * likely one first. Named through the app's own `language` filter, so it
         * reads "Französisch" (or "French") like every other language in the
         * admin — never a bare code.
         *
         * A shop offers the languages it publishes in — settingsStore.form is
         * where this page already has them (the CmsLanguages fragment); the setup
         * store's activeCms does NOT carry them, its Setup.gql leaves the
         * `languages` block commented out.
         *
         * Our own desk writes to tenants whose shops run in any language, so it
         * offers the whole catalog rather than the handful the admin interface
         * happens to be translated into.
         */
        languageOptions() {
            if (this.isPlatform) {
                return _.get(useSettingsStore(), 'optionsWithLocale.language.items') || [];
            }

            return (_.get(useSettingsStore(), 'form.languages') || [])
                .map((language) => _.get(language, 'code'))
                .filter(Boolean)
                .map((code) => ({ code, name: languageName(code, this.locale) }));
        },
    },
    methods: {
        relayout() {
            // Called by the host from inside its own nextTick + frame, so the
            // grid has already sized this panel: measure now, not a tick later.
            const table = this.$refs.table;
            if (table) table.doLayout();
        },
        openPreset(preset) {
            this.drawer.form = preset
                ? { id: preset.id, name: preset.name, body: preset.body, language: preset.language }
                : { id: null, name: '', body: '', language: this.defaultLanguage };
            this.drawer.visible = true;
        },
        /** Ok: into the list, not into the database. */
        applyPreset() {
            const form = { ...this.drawer.form };
            const index = this.presets.findIndex((preset) => preset.id === form.id);
            const presets = [...this.presets];

            if (index === -1) {
                // A placeholder id so the table can key on the row before the
                // server has given it a real one. Negative, so it can never
                // collide with one — and the API drops it, which is what makes
                // the row a create.
                form.id = Math.min(0, ...presets.map((preset) => Number(preset.id) || 0)) - 1;
                presets.push(form);
            } else {
                presets.splice(index, 1, form);
            }

            this.$emit('update:presets', presets);
            this.drawer.visible = false;
            this.relayout();
        },
        /** Also staged: the row goes, the row's deletion happens on Save. */
        removePreset(preset) {
            this.$emit('update:presets', this.presets.filter((candidate) => candidate.id !== preset.id));
        },
        // languages.js and the ISO catalog carry the same 184 codes, so these
        // resolve for every option offered; guarded anyway because a throw here
        // would blank the whole drawer.
        languageFlagFor(code) {
            try {
                return languageFlag(code);
            } catch (error) {
                return '';
            }
        },
        languageNativeNameFor(code) {
            try {
                return languageNativeName(code);
            } catch (error) {
                return '';
            }
        },
    },
};
</script>

<style lang="sass" scoped>
// The section chrome the hosts use for their own sections (Email signatures,
// Auto-replies). Repeated rather than shared because each host declares it in a
// SCOPED block, which cannot reach into this component — that is why the heading
// rendered black here while every heading around it was grey.
.section-intro
    margin-bottom: 1rem

    h4
        margin: 0 0 4px
        color: $color-grey-text

    // Sizing comes from the global `.help` rule — no sizes invented here.
    .help
        margin: 0

// Separates this section from the signatures table above it, the way
// `.signatures-intro` separates that one from the accounts table.
.message-presets-intro
    margin-top: 2.5rem

// Panel chrome comes from the global .settings-panel; only the spacing between
// its parts belongs here.
.message-presets.is-nested
    .settings-panel__hint
        margin: 4px 0 12px

.suggestion-mode
    margin-top: 1rem
    margin-bottom: 0

    .el-form-item
        margin-bottom: 0

    .help
        margin: 4px 0 0

.table-top
    display: flex
    gap: 10px
    margin-bottom: 15px

.muted
    color: var(--color-text-muted, #909399)

// Copied from LocalizationSettings' own scoped block: an el-option's content is
// rendered from THIS component's template, so the layout has to live here too.
.flex-row
    display: flex
    justify-content: space-between

    .col
        display: flex
        align-items: center

        .circle.fi
            margin-right: 0.5rem

    .gray-text
        color: #909399
</style>
